import {DateInput} from '@fullcalendar/core/datelib/env';
import {EventSourceFunc} from '@fullcalendar/core/event-sources/func-event-source';
import classNames from 'classnames';
import fullCalendarJs, {
  Calendar,
  EventApi,
  EventInput,
  FullCalendarJS,
  OptionsInput,
} from 'front-fullcalendarjs';
import _, {debounce, isEqual} from 'lodash';
import {DateTime} from 'luxon';
import {transparentize} from 'polished';
import React, {Component, createRef, RefObject} from 'react';
import {Subject, Subscription} from 'rxjs';
import styled from 'styled-components';

import {buildWindow, globalWindow} from '../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {isElement} from '../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {
  build12Or24hOptionsForFormat,
  TimeFormatsEnum,
} from '../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {filterValidDateTime} from '../../../../libs/shared/core/src/helpers/datetime/luxonHelpers';
import {
  InternationalizationContextProps,
  withInternationalization,
} from '../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontClickEvent} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {insertCleanInnerHtml} from '../../../../libs/shared/core/src/helpers/typescript/cleanHtml';
import {parsePercent} from '../../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {FrameTypesEnum} from '../../../../libs/shared/core/src/models/api/apiTypes';
import {
  CalendarModalActionsEnum,
  CalendarModalTypesEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {LayerContextProps, withLayer} from '../../../../libs/shared/ui/src/components/layers/layerContext';
import {Tooltip} from '../../../../libs/shared/ui/src/components/tooltips/tooltip';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ThemeContextType} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {StartEndData} from '../../../../srcCommon/helpers/calendarHelpers';
import {delayAsync} from '../../../helpers/asyncHelpers';
import {CalendarEventModel} from '../../../models/api/calendarEventModel';
import {CalendarProviderTypesEnum} from '../../../models/api/calendarModel';
import {CalendarEventData} from '../../../store/panels/calendars/calendarsPanelSelectors';
import {
  CalendarEventGridParameters,
  FocusedEventSourcesEnum,
} from '../../../store/panels/calendars/calendarsPanelState';
import {
  CalendarsParameters,
  CalendarsViewTypesEnum,
} from '../../../store/panels/calendars/calendarsParameters';
import {computeNonResourceGuestCount} from '../calendarEventHelpers';
import {buildHoverSubsciption, makeHoverInfo} from './calendarsGridHoverHelpers';
import {fullCalendarCustomStyles} from './fullCalendarCustomStyles';
import {
  buildFullCalendarEventGradientBackground,
  buildFullCalendarEventId,
  buildFullCalendarHoverPlaceholder,
  calendarWeekStartDayToFirstDay,
  fullcalendarDateToLuxonDateTime,
  FullCalendarEvent,
  FullCalendarEventDisplayInfo,
  getFullCalendarViewType,
  getOverlappingEventsWithSchedulingSlot,
  isFullCalendarSegment,
  renderCalendarColumnHeader,
  renderFullCalendarEventContent,
  renderFullCalendarSlotContent,
  updateFullCalendarHoverPlaceholder,
} from './fullCalendarHelpers';
import {FullCalendarStyles} from './fullCalendarStyles';
import {MoreEventsPopover} from './moreEventsPopover';
import {RescheduleCalendarEventDialog} from './rescheduleCalendarEventDialog';

const fullCalendarDayGridDuration = {days: 1};
const fullCalendarSlotLabelFormatBase = {hour: 'numeric'};
const fullCalendarDefaultScrollTime = {hours: 8};

const highlightDurationMillis = 2000;
const animationDurationMillis = 800;

// Debounce timing to recompute events when rapidly changing dates.
const rangeChangeRecomputeEventsDebounceMillis = 200;

/*
 * Types.
 */

export type RescheduleConfirmHandler = (shouldReschedule: boolean, shouldNotifyGuests?: boolean) => void;

export interface SchedulingSlotDefinition {
  id: number;
  start: DateTime;
  end: DateTime;
}

export interface CalendarsGridProps {
  parameters: CalendarsParameters;
  highlightedCalendarEventData: CalendarEventData | undefined;
  focusedCalendarEventData: CalendarEventData | undefined;
  popoverCalendarEventData: CalendarEventData | undefined;
  findFullCalendarEvents: () => ReadonlyArray<FullCalendarEvent>;
  isPreviewMode?: boolean;
  showHoverPlaceholder?: boolean;
  hoverPlaceholderDuration?: number;
  selectedFrameId: string | undefined;
  timeFormat: TimeFormatsEnum;
  timeZone: string;
  shouldCalendarDisplayWeekends: boolean;
  onEventClick: ((parameters: CalendarEventGridParameters) => void) | undefined;
  onHighlightedEventClear: () => void;
  onPopoverEventClear: () => void;
  canCreateEventOnSelect: boolean;
  onCalendarSlotSelect: (data: StartEndData) => void;
  onSchedulingSlotDelete?: (arg: number) => void;
  onSchedulingSlotUpdate?: (slotId: number, startEndData: StartEndData) => void;
  findCalendarEventForCalendarEventVersion: (
    calendarEventVersionUid: string,
  ) => CalendarEventModel | undefined;
  onEventReschedule: (
    parameters: CalendarEventGridParameters,
    startEndData: StartEndData,
    shouldNotifyGuests?: boolean,
  ) => void;
}

/*
 * Style.
 */

const StyledCalendarWrapperDiv = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;

  ${fullCalendarCustomStyles};
`;

interface TimezoneStyleProps {
  $shouldAddTopPadding: boolean;
}
const StyledTimezoneDiv = styled.div<TimezoneStyleProps>`
  position: absolute;
  top: ${(p) => (p.$shouldAddTopPadding ? '60px' : '0px')};
  left: 0px;
  width: 49px;
  height: 45px;
  line-height: 45px;

  text-align: right;
  font-size: ${fontSizes.veryTiny};
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.medium};

  background: linear-gradient(
    ${(p) => p.theme.foreground} 0%,
    ${(p) => p.theme.foreground} 70%,
    ${(p) => transparentize(1, p.theme.foreground)} 100%
  );
`;

/*
 * Component.
 */

interface MoreEventsPopoverInfo {
  dayRect: DOMRectReadOnly;
  fullCalendarEventIds: ReadonlyArray<string>;
  date: Date;
}

interface CalendarsGridState {
  shouldHighlightFocusedEvent: boolean;
  moreEventsPopoverInfo: MoreEventsPopoverInfo | undefined;
  onRescheduleConfirm: RescheduleConfirmHandler | undefined;
  isShowingHoverTooltip: boolean;
  nonResourceGuestCount: number | undefined;
  provider: CalendarProviderTypesEnum | undefined;
}

class CalendarsGridComponent extends Component<
  CalendarsGridProps & InternationalizationContextProps & LayerContextProps & ThemeContextType,
  CalendarsGridState
> {
  constructor(
    props: CalendarsGridProps & InternationalizationContextProps & LayerContextProps & ThemeContextType,
  ) {
    super(props);

    this.fullCalendarJs = fullCalendarJs(buildWindow(props.window), props.window.document);
    this.wrapperRef = createRef<HTMLElement>();
    this.state = {
      shouldHighlightFocusedEvent: false,
      moreEventsPopoverInfo: undefined,
      onRescheduleConfirm: undefined,
      isShowingHoverTooltip: false,
      nonResourceGuestCount: undefined,
      provider: undefined,
    };
    this.gridHoverElement = buildFullCalendarHoverPlaceholder();
  }

  private fullCalendarJs: FullCalendarJS;
  private wrapperRef: RefObject<HTMLElement>;
  private calendar: Calendar | undefined;
  private isMounted: boolean = false;
  private gridHoverSubscription: Subscription | undefined;
  private gridHoverElement: HTMLElement;

  componentDidMount() {
    this.isMounted = true;

    // Prevent event alignment bug by delaying initial render until container has settled.
    globalWindow.setInstrumentedTimeout(() => {
      if (!this.isMounted || !this.wrapperRef.current) {
        return;
      }
      this.calendar = new this.fullCalendarJs.core.Calendar(
        this.wrapperRef.current,
        this.buildFullCalendarOptions(),
      );
      this.calendar.render();
      this.maybeEnableHoverPlaceholder();
    }, 0);

    trackInterfaceEvent(
      this.props.isPreviewMode
        ? InterfaceEventsEnum.VIEW_CALENDAR_PREVIEW
        : InterfaceEventsEnum.VIEW_CALENDAR_GRID,
      {
        view_type: this.props.parameters.viewType,
      },
    );

    this.setupHoverSubsciption();
  }

  private buildFullCalendarOptions = (): OptionsInput => {
    const {parameters, isPreviewMode, timeZone} = this.props;
    const {viewType, focusedDate, weekStartDay} = parameters;

    const isWeekViewCalendar =
      viewType === CalendarsViewTypesEnum.WEEK || viewType === CalendarsViewTypesEnum.SCHEDULING;
    const focusedDateTime = filterValidDateTime(DateTime.fromMillis(focusedDate)) ?? DateTime.now();

    // Calculate the scrollTime in previewMode such that the focused event is centered in the view.
    const scrollTimeHourOffset = focusedDateTime.minute > 15 ? 1 : 2;
    const scrollTime = isPreviewMode
      ? {hours: Math.max(focusedDateTime.hour - scrollTimeHourOffset, 0), minutes: 30}
      : fullCalendarDefaultScrollTime;

    const {timeFormat, shouldCalendarDisplayWeekends, canCreateEventOnSelect} = this.props;
    return {
      plugins: [this.fullCalendarJs.daygrid, this.fullCalendarJs.timegrid, this.fullCalendarJs.interaction],
      // ISO strings are required so fullcalendar can properly handle the `timeZone` field.
      defaultDate: focusedDateTime.toISO(),
      now: () => DateTime.now().toISO(),
      timeZone,
      defaultView: getFullCalendarViewType(viewType),
      events: this.onFullCalendarEventsFetch,
      eventClick: this.onEventClick,
      eventLimit: viewType === CalendarsViewTypesEnum.MONTH,
      eventLimitClick: this.onEventLimitClick,
      eventLimitText: (count: number) => `${count} more`,
      eventRender: this.onEventRender,
      eventPositioned: this.onEventPositioned,
      eventDrop: this.onGridEventReschedule,
      eventResize: this.onGridEventReschedule,
      select: this.onSlotSelect,
      header: false,
      columnHeader: isWeekViewCalendar || viewType === CalendarsViewTypesEnum.MONTH,
      height: 'parent' as any,
      slotEventOverlap: false,
      slotLabelFormat: {
        ...fullCalendarSlotLabelFormatBase,
        ...build12Or24hOptionsForFormat(timeFormat),
      },
      scrollTime,
      duration: fullCalendarDayGridDuration,
      allDayText: '',
      selectable: !isPreviewMode && canCreateEventOnSelect,
      columnHeaderHtml: isWeekViewCalendar ? (d) => renderCalendarColumnHeader(d, timeZone) : undefined,
      fixedWeekCount: false,
      nowIndicator: true,
      displayEventTime: false,
      weekends: shouldCalendarDisplayWeekends,
      firstDay: calendarWeekStartDayToFirstDay(weekStartDay),
      eventAllow: this.shouldAllowEventReschedule,
      eventOrder: 'isTimeSlot,start,-duration,allDay,title',
      selectAllow: this.shouldAllowSelection,
    };
  };

  componentDidUpdate(prevProps: Readonly<CalendarsGridProps>, prevState: Readonly<CalendarsGridState>) {
    if (!this.calendar) {
      return;
    }

    const hasNewHighlightedEvent = !isEqual(
      this.props.highlightedCalendarEventData,
      prevProps.highlightedCalendarEventData,
    );
    const hasNewFocusedEvent = !isEqual(
      this.props.focusedCalendarEventData,
      prevProps.focusedCalendarEventData,
    );
    const hasNewPopoverEvent = !isEqual(
      this.props.popoverCalendarEventData,
      prevProps.popoverCalendarEventData,
    );

    // Batch and manually handle all changes to FullCalendar to prevent unnecessary re-renders.
    this.calendar.batchRendering(() => {
      if (!this.calendar) {
        return;
      }

      const {parameters, timeFormat, timeZone, shouldCalendarDisplayWeekends} = this.props;
      const {viewType, focusedDate, weekStartDay} = parameters;

      const hasNewEventsShallow = this.props.findFullCalendarEvents !== prevProps.findFullCalendarEvents;
      const hasNewPreviewMode = this.props.isPreviewMode !== prevProps.isPreviewMode;
      const hasNewFocusedDate = focusedDate !== prevProps.parameters.focusedDate;
      const hasNewViewType = viewType !== prevProps.parameters.viewType;
      const hasNewTimeZone = timeZone !== prevProps.timeZone;

      if (hasNewFocusedDate) {
        this.calendar.gotoDate(DateTime.fromMillis(focusedDate).toISO());
      } else if (hasNewEventsShallow || hasNewHighlightedEvent || hasNewFocusedEvent || hasNewPopoverEvent) {
        this.calendar.refetchEvents();
      }

      if (hasNewViewType) {
        const isWeekViewCalendar = viewType === CalendarsViewTypesEnum.WEEK;
        this.calendar.changeView(getFullCalendarViewType(viewType));
        this.calendar.setOption('eventLimit', viewType === CalendarsViewTypesEnum.MONTH);
        this.calendar.setOption(
          'columnHeader',
          isWeekViewCalendar || viewType === CalendarsViewTypesEnum.MONTH,
        );
        this.calendar.setOption(
          'columnHeaderHtml',
          isWeekViewCalendar
            ? (d: DateInput) => renderCalendarColumnHeader(d, this.props.timeZone)
            : undefined,
        );
      }

      if (hasNewPreviewMode || this.props.canCreateEventOnSelect !== prevProps.canCreateEventOnSelect) {
        this.calendar.setOption('selectable', !this.props.isPreviewMode && this.props.canCreateEventOnSelect);
      }

      // Don't allow calendar to be selected while the more events popover is open.
      const isMoreEventsPopoverOpen = Boolean(this.state.moreEventsPopoverInfo);
      const wasMoreEventsPopoverOpen = Boolean(prevState.moreEventsPopoverInfo);
      if (!wasMoreEventsPopoverOpen && isMoreEventsPopoverOpen) {
        this.calendar.setOption('selectable', !isMoreEventsPopoverOpen);
      }

      if (timeFormat !== prevProps.timeFormat) {
        this.calendar.setOption('slotLabelFormat', {
          ...fullCalendarSlotLabelFormatBase,
          ...build12Or24hOptionsForFormat(timeFormat),
        });
      }

      if (prevProps.shouldCalendarDisplayWeekends !== shouldCalendarDisplayWeekends) {
        this.calendar.setOption('weekends', shouldCalendarDisplayWeekends);
      }

      if (prevProps.parameters.weekStartDay !== weekStartDay) {
        const newFirstDay = calendarWeekStartDayToFirstDay(weekStartDay);
        this.calendar.setOption('firstDay', newFirstDay);
      }

      if (hasNewTimeZone) {
        this.calendar.setOption('timeZone', timeZone);
      }
    });

    this.maybeEnableHoverPlaceholder();
    this.maybeCloseMoreEventsPopover(prevProps.focusedCalendarEventData);
    this.maybeResetMonthView(prevProps.parameters.viewType);
    this.maybeUpdateCalendarSize(prevProps);
    this.maybeHighlightFocusedEvent(hasNewHighlightedEvent);
  }

  componentWillUnmount() {
    this.isMounted = false;

    if (this.gridHoverSubscription) {
      this.gridHoverSubscription.unsubscribe();
    }

    if (this.calendar) {
      this.calendar.destroy();
    }
  }

  private readonly wrapper$ = new Subject<HTMLElement | null>();
  private hoverInfo$ = makeHoverInfo(this.wrapper$, () => this.props.hoverPlaceholderDuration || 30);

  private maybeEnableHoverPlaceholder = () =>
    this.wrapper$.next(this.props.showHoverPlaceholder ? this.wrapperRef.current : null);

  setupHoverSubsciption() {
    this.gridHoverSubscription = buildHoverSubsciption(
      this.hoverInfo$,
      (info) => {
        // Remove the element either way, because it prevents style trashing before we append it to a new parent.
        this.gridHoverElement.remove();

        if (info === undefined) {
          return;
        }

        updateFullCalendarHoverPlaceholder(this.gridHoverElement, info.y, info.height);
        info.dateElement.appendChild(this.gridHoverElement);
      },
      (isShowingHoverTooltip) => this.setState({isShowingHoverTooltip}),
    );
  }

  private shouldAllowEventReschedule = (
    dropInfo: {allDay: boolean; start: Date; end: Date},
    event: EventApi | null,
  ) => {
    if (!dropInfo || !event) {
      return false;
    }

    if (dropInfo.allDay !== event.allDay) {
      // Do not allow dragging between the all-day section and vice versa.
      return false;
    }

    if (event.extendedProps.isTimeSlot) {
      // Only allow scheduling slots that are in the future.
      return dropInfo.end.getTime() > DateTime.now().valueOf();
    }

    return true;
  };

  private shouldAllowSelection = (selectInfo: {start: Date; end: Date}) => {
    const {viewType} = this.props.parameters;
    if (viewType === CalendarsViewTypesEnum.SCHEDULING && this.calendar) {
      const endDateTime = fullcalendarDateToLuxonDateTime(selectInfo.end, this.calendar);

      // Only allow scheduling slots that are in the future.
      return endDateTime > DateTime.now();
    }

    // Otherwise, allow the selection.
    return true;
  };

  private maybeCloseMoreEventsPopover = (prevFocusedCalendarEventData: CalendarEventData | undefined) => {
    // Close the more events popover if we begin editing an event.
    if (
      this.state.moreEventsPopoverInfo &&
      this.props.focusedCalendarEventData &&
      !prevFocusedCalendarEventData
    ) {
      this.setState({moreEventsPopoverInfo: undefined});
    }
  };

  private maybeResetMonthView = (prevViewType: CalendarsViewTypesEnum) => {
    // If we're switching away from month view, restore scrollTime and close the more events popover if needed.
    if (
      prevViewType === CalendarsViewTypesEnum.MONTH &&
      this.props.parameters.viewType !== CalendarsViewTypesEnum.MONTH
    ) {
      if (this.calendar) {
        this.calendar.scrollToTime(fullCalendarDefaultScrollTime);
      }
      if (this.state.moreEventsPopoverInfo) {
        this.setState({moreEventsPopoverInfo: undefined});
      }
    }
  };

  private readonly maybeUpdateCalendarSize = (prevProps: CalendarsGridProps) => {
    // Whenever the sidePanel is expanded to display the calendar, ensure that the calendar height is recomputed properly.
    if (
      !this.props.isPreviewMode &&
      this.props.selectedFrameId === FrameTypesEnum.CALENDAR &&
      prevProps.selectedFrameId !== FrameTypesEnum.CALENDAR &&
      this.calendar
    ) {
      this.calendar.updateSize();
    }
  };

  private readonly maybeHighlightFocusedEvent = async (hasNewHighlightedEvent: boolean) => {
    // If a new focused event has been set by the calendar preview widget, highlight it:
    // 1. Scroll the calendar to the event and set a state variable to add the css className that highlights the focused event.
    // 2. After a delay, unset the state variable.
    // 3. After a slight delay, clear the focused event. This final delay allows the
    //    event shading to animate from highlighted to normal before the calendar is re-rendered.

    if (this.props.highlightedCalendarEventData && hasNewHighlightedEvent) {
      this.setState({shouldHighlightFocusedEvent: true});

      if (this.calendar) {
        this.calendar.scrollToTime({
          hours: DateTime.fromMillis(this.props.parameters.focusedDate).hour,
        });
      }

      await delayAsync(highlightDurationMillis);

      // Prevent state updates on unmounted component since this happening asynchronously.
      if (!this.isMounted) {
        return;
      }

      this.setState({shouldHighlightFocusedEvent: false});

      await delayAsync(animationDurationMillis);

      if (!this.isMounted) {
        return;
      }

      if (this.props.onHighlightedEventClear && !this.props.isPreviewMode) {
        this.props.onHighlightedEventClear();
      }
    }
  };

  private readonly findFullCalendarEventsDebounce = debounce(
    (worker: () => void) => worker(),
    rangeChangeRecomputeEventsDebounceMillis,
  );

  private lastInfo: {start?: number; end?: number} = {};
  private readonly onFullCalendarEventsFetch: EventSourceFunc = (info, callback) => {
    const {findFullCalendarEvents} = this.props;

    const sendFullCalendarEventsToCallback = () => {
      const fullCalendarEvents = findFullCalendarEvents();
      callback(fullCalendarEvents as EventInput[]);
    };

    // When the range changes, we debounce the refetch.
    const newInfo = {start: info.start.getTime(), end: info.end.getTime()};
    const {start, end} = this.lastInfo;
    this.lastInfo = newInfo;

    if (start !== newInfo.start || end !== newInfo.end) {
      this.findFullCalendarEventsDebounce(sendFullCalendarEventsToCallback);
      return;
    }

    // Otherwise, perform it right away.
    this.findFullCalendarEventsDebounce.cancel();
    sendFullCalendarEventsToCallback();
  };

  private readonly onEventClick = (info: {event: EventApi; jsEvent: MouseEvent; el: HTMLElement}) => {
    const {extendedProps, id} = info.event;

    if (extendedProps.isTimeSlot && this.props.onSchedulingSlotDelete) {
      this.props.onSchedulingSlotDelete(extendedProps.slotId);
      return;
    }

    if (!this.props.onEventClick) {
      return;
    }

    this.props.onEventClick({
      calendarEventVersionUid: extendedProps.calendarEventVersionUid,
      calendarVersionId: extendedProps.calendarVersionId,
      instanceStartDate: extendedProps.instanceStartDate,
      source: FocusedEventSourcesEnum.CALENDAR_GRID,
      arbitraryCalendarEventId: extendedProps.isArbitraryEvent ? id : undefined,
      overrides: {},
    });
  };

  private readonly onEventRender = (info: {event: EventApi; el: HTMLElement}) => {
    const {el, event} = info;
    el.setAttribute('id', event.id);
    if (!this.calendar) {
      return;
    }

    // Don't apply the background gradient to standard events in month.
    if (
      event.extendedProps.gradientColor &&
      (event.allDay || this.props.parameters.viewType !== CalendarsViewTypesEnum.MONTH)
    ) {
      el.style.background = buildFullCalendarEventGradientBackground(
        event.extendedProps.gradientColor,
        this.props.theme,
      );
    }
    if (!event.start || !event.end) {
      return;
    }

    if (event.extendedProps.isTimeSlot) {
      insertCleanInnerHtml(
        el,
        renderFullCalendarSlotContent(event.start, event.end, this.props.timeFormat, this.props.timeZone),
      );
      return;
    }

    const eventDisplayInfo: FullCalendarEventDisplayInfo = {
      startDate: fullcalendarDateToLuxonDateTime(event.start, this.calendar),
      endDate: fullcalendarDateToLuxonDateTime(event.end, this.calendar),
      isAllDay: event.allDay,
      summary: event.extendedProps.summary,
      durationMinutes: event.extendedProps.durationMinutes,
      accessRole: event.extendedProps.calendarVersionAccessRole,
      dotBackgroundColor: event.extendedProps.dotBackgroundColor,
      dotBorderColor: event.extendedProps.dotBorderColor,
      isDurationEditable: event.durationEditable,
      isPrivateEvent: event.extendedProps.isPrivateEvent,
      arbitraryEventShowAs: event.extendedProps.showAs,
    };

    // This is still the recommended way to modify the content within a fullcalendar
    // event block, but the maintainers are considering adding an option to return a JSX
    // element. See https://github.com/fullcalendar/fullcalendar-react/issues/12.
    insertCleanInnerHtml(
      el,
      renderFullCalendarEventContent(
        eventDisplayInfo,
        this.props.parameters.viewType,
        this.props.timeFormat,
        this.props.localize,
      ),
    );
  };

  private readonly onEventPositioned = (info: {event: EventApi; el: HTMLElement; isMirror: boolean}) => {
    const {extendedProps} = info.event;
    if (!extendedProps.isTimeSlot) {
      // We don't need extra repositioning for regular events.
      return;
    }

    const slotElement = info.el;

    if (info.isMirror) {
      // If we're dragging or resizing, render with full width.
      slotElement.style.left = '0%';
      slotElement.style.right = '0%';
      return;
    }

    const overlappingEvents = getOverlappingEventsWithSchedulingSlot(slotElement);
    if (overlappingEvents.length === 0) {
      // We don't need to reposition since there are no overlaps.
      return;
    }

    // Put the scheduling slot in the right 80% of the width.
    slotElement.style.left = '20%';
    slotElement.style.right = '0%';

    // Find the farthest left and right positions of the overlapping events.
    const farthestEventLeft = Math.min(
      ...overlappingEvents.map((element) => parsePercent(element.style.left)),
    );
    const farthestEventRight = Math.min(
      ...overlappingEvents.map((element) => parsePercent(element.style.right)),
    );

    const totalEventWidth = 100 - farthestEventLeft - farthestEventRight;

    // The amount we must shrink by so the remaining events take up 20% of the width.
    const shrinkFactor = 20 / totalEventWidth;

    overlappingEvents.forEach((element) => {
      const eventWidth = 100 - parsePercent(element.style.right) - parsePercent(element.style.left);
      const newLeft = (parsePercent(element.style.left) - farthestEventLeft) * shrinkFactor;
      const newRight = 100 - newLeft - eventWidth * shrinkFactor;
      // eslint-disable-next-line no-param-reassign
      element.style.left = `${newLeft}%`;
      // eslint-disable-next-line no-param-reassign
      element.style.right = ` ${newRight}%`;
    });
  };

  private readonly onGridEventReschedule = (info: {event: EventApi}) => {
    const {extendedProps, start, end, allDay} = info.event;
    if (!start || !end || !this.calendar) {
      return;
    }

    const startEndData: StartEndData = {
      startDateTime: fullcalendarDateToLuxonDateTime(start, this.calendar),
      endDateTime: fullcalendarDateToLuxonDateTime(end, this.calendar),
      isAllDay: allDay,
    };

    // If we're rescheduling a scheduling time slot, let the parent know.
    if (extendedProps.isTimeSlot && this.props.onSchedulingSlotUpdate) {
      this.props.onSchedulingSlotUpdate(extendedProps.slotId, startEndData);
      return;
    }

    // If we're currently editing this event, just inform the parent of the start/end change.
    if (extendedProps.isFocusedEvent) {
      this.props.onCalendarSlotSelect(startEndData);
      return;
    }

    const eventParameters: CalendarEventGridParameters = {
      calendarEventVersionUid: extendedProps.calendarEventVersionUid,
      calendarVersionId: extendedProps.calendarVersionId,
      instanceStartDate: extendedProps.instanceStartDate,
      source: FocusedEventSourcesEnum.CALENDAR_GRID,
      arbitraryCalendarEventId: undefined,
      overrides: {},
    };

    // If this event has attendees other than the organizer, show a confirmation prompt.
    const calendarEvent = this.props.findCalendarEventForCalendarEventVersion(
      extendedProps.calendarEventVersionUid,
    );
    if (!calendarEvent) {
      return;
    }

    const nonResourceGuestCount = computeNonResourceGuestCount(calendarEvent.participants);
    if (nonResourceGuestCount > 0) {
      const onRescheduleConfirm = (shouldReschedule: boolean, shouldNotifyGuests?: boolean) => {
        trackInterfaceEvent(InterfaceEventsEnum.CALENDAR_EVENT_MODAL_CLICK, {
          calendar_event_uid: calendarEvent.uid,
          modal_type: CalendarModalTypesEnum.RESCHEDULE,
          action: shouldReschedule ? CalendarModalActionsEnum.CONFIRM : CalendarModalActionsEnum.CANCEL,
          notify_attendees: shouldNotifyGuests,
          calendar_provider: extendedProps.provider,
        });

        if (shouldReschedule) {
          this.props.onEventReschedule(eventParameters, startEndData, shouldNotifyGuests);
        }

        // Reset our grid state.
        if (this.calendar) {
          this.calendar.refetchEvents();
        }
        this.setState({
          onRescheduleConfirm: undefined,
          nonResourceGuestCount: undefined,
          provider: undefined,
        });
      };

      this.setState({
        onRescheduleConfirm,
        nonResourceGuestCount,
        provider: extendedProps.provider,
      });
      return;
    }

    // Otherwise save immediately.
    this.props.onEventReschedule(eventParameters, startEndData);
  };

  private readonly onSlotSelect = (selectionInfo: {start: Date; end: Date; allDay: boolean}) => {
    const {start, end, allDay} = selectionInfo;

    if (this.props.onCalendarSlotSelect && this.calendar) {
      this.props.onCalendarSlotSelect({
        startDateTime: fullcalendarDateToLuxonDateTime(start, this.calendar),
        endDateTime: fullcalendarDateToLuxonDateTime(end, this.calendar),
        isAllDay: allDay,
      });
    }

    // Unselect fullcalendar.
    if (this.calendar) {
      this.calendar.unselect();
    }
  };

  // Invoked when the user clicks the "n more" events button in month view.
  private readonly onEventLimitClick = (info: {date: Date; dayEl: HTMLElement; segs: any[]}) => {
    const fullCalendarEventIds = _(info.segs)
      .map((maybeSegment) => {
        // Manually validate the type since FullCalendar's behavior does not match documentation for this callback.
        if (isFullCalendarSegment(maybeSegment)) {
          const {calendarEventVersionUid, calendarVersionId, isArbitraryEvent, instanceStartDate} =
            maybeSegment.eventRange.def.extendedProps;
          return isArbitraryEvent
            ? maybeSegment.el.id
            : buildFullCalendarEventId(calendarEventVersionUid, calendarVersionId, instanceStartDate);
        }

        return undefined;
      })
      .compact()
      .value();

    this.setState({
      moreEventsPopoverInfo: {
        dayRect: info.dayEl.getBoundingClientRect(),
        fullCalendarEventIds,
        date: info.date,
      },
    });
  };

  private readonly onMorePopoverRequestClose = () => {
    this.setState({moreEventsPopoverInfo: undefined});
    if (this.props.onPopoverEventClear) {
      this.props.onPopoverEventClear();
    }
  };

  private maybeRenderPlaceholderTooltip() {
    if (!this.state.isShowingHoverTooltip) {
      return null;
    }

    return (
      <Tooltip
        anchor={() => this.gridHoverElement.getBoundingClientRect()}
        placement="top"
        maxWidth={216}
        isMultiline
      >
        <Trans id="calendargrid-scheduling-placeholdertooltip">
          Click and drag on the calendar to create available meeting blocks
        </Trans>
      </Tooltip>
    );
  }

  render() {
    const isPreviewMode = Boolean(this.props.isPreviewMode);
    const isWeekViewCalendar = this.props.parameters.viewType === CalendarsViewTypesEnum.WEEK;

    return (
      <StyledCalendarWrapperDiv
        className={buildContainerClassName(
          this.props.parameters.viewType,
          isPreviewMode,
          this.state.shouldHighlightFocusedEvent,
        )}
        onMouseDown={(e: FrontClickEvent) => {
          if (!this.state.moreEventsPopoverInfo) {
            return;
          }

          // Dismiss the more events popover on click unless we're clicking another "more" link.
          const eventTarget = e && (e.target as Node);
          if (isElement(eventTarget) && eventTarget.classList.contains('fc-more')) {
            return;
          }

          this.setState({moreEventsPopoverInfo: undefined});
        }}
      >
        <FullCalendarStyles />
        <div ref={this.wrapperRef as any} />
        {this.props.parameters.viewType !== CalendarsViewTypesEnum.MONTH && (
          <StyledTimezoneDiv $shouldAddTopPadding={isWeekViewCalendar}>
            {DateTime.local().toFormat('ZZZZ')}
          </StyledTimezoneDiv>
        )}
        {maybeRenderMonthViewPopover(
          this.props,
          this.state.moreEventsPopoverInfo,
          this.onMorePopoverRequestClose,
        )}
        {maybeRenderRescheduleDialog(
          this.state.onRescheduleConfirm,
          this.state.nonResourceGuestCount,
          this.state.provider,
        )}
        {this.maybeRenderPlaceholderTooltip()}
      </StyledCalendarWrapperDiv>
    );
  }
}

function buildContainerClassName(
  viewType: CalendarsViewTypesEnum,
  isPreviewMode: boolean,
  shouldHighlightFocusedEvent: boolean,
) {
  const isFullScreen = [CalendarsViewTypesEnum.WEEK, CalendarsViewTypesEnum.MONTH].includes(viewType);

  return classNames('fullcalendar-wrapper', {
    'fc-fullscreen': isFullScreen,
    'fc-wrapper-month-view': viewType === CalendarsViewTypesEnum.MONTH,
    'fc-wrapper-preview-mode': isPreviewMode,
    'fc-highlight-mode': shouldHighlightFocusedEvent || isPreviewMode,
  });
}

function maybeRenderMonthViewPopover(
  props: CalendarsGridProps & InternationalizationContextProps,
  info: MoreEventsPopoverInfo | undefined,
  onRequestClose: () => void,
) {
  if (!info) {
    return null;
  }

  const events = _(props.findFullCalendarEvents())
    .filter((e) => e.id && info.fullCalendarEventIds.includes(e.id))
    .sortBy((e) => (e.allDay ? 0 : e.start && e.start.valueOf()))
    .value();

  return (
    <MoreEventsPopover
      dayRect={info.dayRect}
      date={info.date}
      fullCalendarEvents={events}
      timeFormat={props.timeFormat}
      timeZone={props.timeZone}
      localize={props.localize}
      onRequestClose={onRequestClose}
      onEventClick={props.onEventClick}
    />
  );
}

function maybeRenderRescheduleDialog(
  onRescheduleConfirm: RescheduleConfirmHandler | undefined,
  nonResourceGuestCount: number | undefined,
  provider?: CalendarProviderTypesEnum,
) {
  if (!onRescheduleConfirm || nonResourceGuestCount === undefined || provider === undefined) {
    return null;
  }

  return (
    <RescheduleCalendarEventDialog
      onRescheduleConfirm={onRescheduleConfirm}
      nonResourceGuestCount={nonResourceGuestCount}
      shouldShowNotifyGuestsOption={provider === CalendarProviderTypesEnum.GOOGLE}
    />
  );
}

export const CalendarsGrid = withLayer(withInternationalization(CalendarsGridComponent));
